import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Rating,
  alpha,
  useTheme
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Block as BlockIcon,
  Reply as ReplyIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";

const mockReviews = [
  {
    id: 1,
    customer: {
      name: "Sarah Johnson",
      avatar: "",
      email: "sarah.j@example.com"
    },
    cleaner: {
      name: "Maria Garcia",
      avatar: ""
    },
    rating: 5,
    comment: "Excellent service! Maria was very thorough and professional. My apartment has never been cleaner!",
    date: "2024-03-20",
    status: "Approved",
    service: "Deep Cleaning",
    response: "Thank you for your kind words, Sarah! It was a pleasure serving you."
  },
  {
    id: 2,
    customer: {
      name: "Mike Chen",
      avatar: "",
      email: "mike.chen@example.com"
    },
    cleaner: {
      name: "James Wilson",
      avatar: ""
    },
    rating: 4,
    comment: "Good service overall. James was punctual and did a decent job. Some areas could use more attention.",
    date: "2024-03-19",
    status: "Approved",
    service: "Standard Cleaning",
    response: null
  },
  {
    id: 3,
    customer: {
      name: "Emily Davis",
      avatar: "",
      email: "emily.d@example.com"
    },
    cleaner: {
      name: "Carlos Rodriguez",
      avatar: ""
    },
    rating: 2,
    comment: "Disappointed with the service. The cleaner arrived 30 minutes late and missed several areas.",
    date: "2024-03-18",
    status: "Pending",
    service: "Move-out Cleaning",
    response: null
  },
  {
    id: 4,
    customer: {
      name: "David Wilson",
      avatar: "",
      email: "david.w@example.com"
    },
    cleaner: {
      name: "Maria Garcia",
      avatar: ""
    },
    rating: 5,
    comment: "Outstanding work! Maria pays attention to every detail. Will definitely book again.",
    date: "2024-03-17",
    status: "Approved",
    service: "Standard Cleaning",
    response: "We're thrilled to hear you had a great experience! Looking forward to your next booking."
  }
];

const StatusTypes = {
  ALL: 'all',
  APPROVED: 'Approved',
  PENDING: 'Pending',
  REJECTED: 'Rejected'
};

const RatingTypes = {
  ALL: 'all',
  FIVE: 5,
  FOUR: 4,
  THREE: 3,
  TWO: 2,
  ONE: 1
};

export default function Reviews() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [statusFilter, setStatusFilter] = useState(StatusTypes.ALL);
  const [ratingFilter, setRatingFilter] = useState(RatingTypes.ALL);

  const filteredReviews = mockReviews.filter(review => {
    const matchesSearch = review.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         review.cleaner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         review.comment.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === StatusTypes.ALL || review.status === statusFilter;
    const matchesRating = ratingFilter === RatingTypes.ALL || review.rating === ratingFilter;
    
    return matchesSearch && matchesStatus && matchesRating;
  });

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedReviews(filteredReviews.map(r => r.id));
    } else {
      setSelectedReviews([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedReviews.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedReviews, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedReviews.slice(1));
    } else if (selectedIndex === selectedReviews.length - 1) {
      newSelected = newSelected.concat(selectedReviews.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedReviews.slice(0, selectedIndex),
        selectedReviews.slice(selectedIndex + 1),
      );
    }
    setSelectedReviews(newSelected);
  };

  const isSelected = (id) => selectedReviews.indexOf(id) !== -1;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved': return <ApprovedIcon />;
      case 'Pending': return <PendingIcon />;
      case 'Rejected': return <BlockIcon />;
      default: return <PendingIcon />;
    }
  };

  const stats = [
    { label: 'Total Reviews', value: '1,245', change: '+8%' },
    { label: 'Average Rating', value: '4.7/5', change: '+0.1' },
    { label: 'Response Rate', value: '89%', change: '+5%' },
    { label: 'Pending Review', value: '12', change: '-3' },
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Reviews & Ratings
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
            }}
          >
            Refresh
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage customer reviews, ratings, and responses
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <Chip 
                  label={stat.change} 
                  size="small"
                  color={stat.change.startsWith('+') ? "success" : "error"}
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls Section */}
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search reviews..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Filters
            </Button>

            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={() => setFilterMenuAnchor(null)}
            >
              <MenuItem>
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Status:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[StatusTypes.ALL, StatusTypes.APPROVED, StatusTypes.PENDING, StatusTypes.REJECTED].map(status => (
                    <Chip
                      key={status}
                      label={status}
                      size="small"
                      color={statusFilter === status ? "primary" : "default"}
                      onClick={() => setStatusFilter(status)}
                    />
                  ))}
                </Box>
              </MenuItem>
              <MenuItem>
                <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Rating:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[RatingTypes.ALL, RatingTypes.FIVE, RatingTypes.FOUR, RatingTypes.THREE, RatingTypes.TWO, RatingTypes.ONE].map(rating => (
                    <Chip
                      key={rating}
                      label={rating === RatingTypes.ALL ? 'All' : `${rating} Stars`}
                      size="small"
                      color={ratingFilter === rating ? "primary" : "default"}
                      onClick={() => setRatingFilter(rating)}
                    />
                  ))}
                </Box>
              </MenuItem>
            </Menu>

            <Box sx={{ flexGrow: 1 }} />

            {selectedReviews.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" color="success">
                  Approve
                </Button>
                <Button variant="outlined" size="small" color="error">
                  Reject
                </Button>
                <Chip 
                  label={`${selectedReviews.length} selected`} 
                  color="primary" 
                  variant="outlined"
                  onDelete={() => setSelectedReviews([])}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Reviews Table */}
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedReviews.length > 0 && selectedReviews.length < filteredReviews.length}
                    checked={filteredReviews.length > 0 && selectedReviews.length === filteredReviews.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Review Details</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Rating</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Service</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Date</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Response</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReviews
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((review) => {
                  const isItemSelected = isSelected(review.id);
                  return (
                    <TableRow
                      key={review.id}
                      hover
                      selected={isItemSelected}
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, review.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 400 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Avatar src={review.customer.avatar} sx={{ width: 32, height: 32 }}>
                              {review.customer.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="600">
                                {review.customer.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                for {review.cleaner.name}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                            {review.comment}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating value={review.rating} size="small" readOnly />
                          <Typography variant="body2" fontWeight="600">
                            {review.rating}/5
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={review.service} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={review.status}
                          size="small"
                          color={getStatusColor(review.status)}
                          icon={getStatusIcon(review.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(review.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {review.response ? (
                          <Chip 
                            label="Replied" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                          />
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ReplyIcon />}
                            sx={{ borderRadius: 2 }}
                          >
                            Reply
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredReviews.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        />
      </Card>
    </Box>
  );
}